// 供应商选择状态管理

import type { SupplierStageCode } from "@/constants";
import type { SourcingNodeConfig } from "@/pages/requirementManagement/sourcingRequirementManagement/type";
import type { SupplierUserSimpleListItem } from "@/types/apis";
import type { FormInstance } from "antd";
import { useCallback, useState } from "react";

// 负责管理供应商选择弹窗的开关状态、当前操作的字段、以及已选供应商的详情映射
export function useSupplierSelection(form: FormInstance) {
  // 弹窗开关状态
  const [modalOpen, setModalOpen] = useState(false);
  // 当前打开弹窗对应的字段名（用于知道往哪个字段添加供应商）
  const [currentField, setCurrentField] = useState<string>("");
  // 当前供应商阶段筛选条件
  const [currentSupplierStage, setCurrentSupplierStage] = useState<
    SupplierStageCode | undefined
  >();
  // 供应商详情映射 {字段名: 供应商详情数组}，用于回显供应商名称
  const [suppliersMap, setSuppliersMap] = useState<
    Record<string, SupplierUserSimpleListItem[]>
  >({});

  // 关闭弹窗
  const closeModal = useCallback(() => setModalOpen(false), []);

  // 打开供应商选择弹窗，记录当前操作的字段和阶段筛选
  const openModal = useCallback(
    (fieldName: string, supplierStage?: SupplierStageCode) => {
      setCurrentField(fieldName);
      setCurrentSupplierStage(supplierStage);
      setModalOpen(true);
    },
    [],
  );

  // 初始化供应商列表（用于数据回显）
  // 将后端返回的 supplierList 转换为完整格式，并同步到表单值和 suppliersMap
  const initSuppliersMap = useCallback(
    (
      supplierList: NonNullable<SourcingNodeConfig["extra"]>["supplierList"],
      fieldName: string,
    ) => {
      if (!supplierList || supplierList.length === 0) return;

      const suppliers: SupplierUserSimpleListItem[] = supplierList.map((s) => ({
        id: s.id,
        supplierName: s.supplierName,
        supplierUsName: s.supplierUsName,
        supplierCode: "",
        supplierStage: 0,
      }));

      setSuppliersMap((prev) => ({
        ...prev,
        [fieldName]: suppliers,
      }));

      form.setFieldValue(
        fieldName,
        supplierList.map((s) => s.id),
      );
    },
    [form],
  );

  // 添加选中的供应商（去重后添加到表单和映射中）
  const addSuppliers = useCallback(
    (newSuppliers: SupplierUserSimpleListItem[]) => {
      if (!currentField) return;

      const existingIds = form.getFieldValue(currentField) || [];
      const suppliersToAdd = newSuppliers.filter(
        (supplier) => !existingIds.includes(supplier.id),
      );
      if (suppliersToAdd.length === 0) return;

      form.setFieldValue(currentField, [
        ...existingIds,
        ...suppliersToAdd.map((s) => s.id),
      ]);

      setSuppliersMap((prev) => ({
        ...prev,
        [currentField]: [...(prev[currentField] || []), ...suppliersToAdd],
      }));
    },
    [currentField, form],
  );

  // 移除指定供应商（从表单值和映射中删除）
  const removeSupplier = useCallback(
    (fieldName: string, supplierId: string) => {
      const ids = form.getFieldValue(fieldName) || [];
      form.setFieldValue(
        fieldName,
        ids.filter((id: number | string) => id !== supplierId),
      );

      setSuppliersMap((prev) => ({
        ...prev,
        [fieldName]: prev[fieldName]?.filter((s) => s.id !== supplierId) || [],
      }));
    },
    [form],
  );

  // 获取指定字段的供应商详情列表
  const getSuppliers = useCallback(
    (fieldName: string) => suppliersMap[fieldName] || [],
    [suppliersMap],
  );

  return {
    modalOpen,
    openModal,
    addSuppliers,
    removeSupplier,
    getSuppliers,
    initSuppliersMap,
    currentSupplierStage,
    closeModal,
  };
}
